import { useState } from 'react';
import { CoachLayout } from '@/components/coach/layout/CoachLayout';
import { sportConfig } from '@/lib/coachConstants';
import type { SportType } from '@/lib/coachConstants';
import { useCoachAthletes } from '@/hooks/useCoachAthletes';
import type { CoachAthleteItem } from '@/lib/templateApi';
import { cn } from '@/lib/utils';
import { Users, Search, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useNavigate } from 'react-router-dom';

const statusConfig = {
  active: { label: 'Active', color: 'hsl(150 60% 45%)' },
  inactive: { label: 'Inactive', color: 'hsl(220 10% 55%)' },
};

export default function AthletesPage() {
  const { data: athletes = [], isLoading } = useCoachAthletes();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const navigate = useNavigate();

  const filtered = athletes.filter((a: CoachAthleteItem) => {
    const nameMatch = a.name?.toLowerCase().includes(search.toLowerCase()) ?? false;
    const emailMatch = a.email?.toLowerCase().includes(search.toLowerCase()) ?? false;
    if (search && !nameMatch && !emailMatch) return false;
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    return true;
  });

  const getInitials = (name: string | null, email: string | null) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    if (email) return email[0].toUpperCase();
    return '?';
  };

  if (isLoading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-48">
          <p className="text-xs text-muted-foreground font-mono">Loading athletes...</p>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Athletes</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{athletes.length} athletes in your roster</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search athletes..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-card/30 border border-border/20 rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:border-accent/40 focus:outline-none transition-colors" />
          </div>
          <div className="flex gap-1.5">
            {(['all', 'active', 'inactive'] as const).map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={cn(
                  'text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-md border transition-all',
                  statusFilter === s ? 'border-accent/40 bg-accent/10 text-accent' : 'border-border/20 text-muted-foreground hover:text-foreground hover:border-border/40',
                )}>
                {s === 'all' ? 'All' : statusConfig[s].label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          {(['active', 'inactive'] as const).map(status => {
            const count = athletes.filter((a: CoachAthleteItem) => a.status === status).length;
            const cfg = statusConfig[status];
            return (
              <div key={status} className="rounded-lg border border-border/15 bg-card/20 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-2 w-2 rounded-full" style={{ background: cfg.color }} />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{cfg.label}</span>
                </div>
                <p className="text-2xl font-semibold text-foreground">{count}</p>
              </div>
            );
          })}
        </div>

        {/* Athlete List */}
        <div className="space-y-2">
          {filtered.map((athlete: CoachAthleteItem) => {
            const sport = athlete.sport ? sportConfig[athlete.sport as SportType] : null;
            const status = statusConfig[athlete.status];
            return (
              <div key={athlete.id} className="rounded-lg border border-border/15 bg-card/20 p-4 hover:bg-card/30 transition-all">
                <div className="flex items-center gap-4">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="text-xs bg-accent/10 text-accent font-mono">
                      {getInitials(athlete.name, athlete.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-foreground">{athlete.name ?? athlete.email ?? 'Unknown'}</h3>
                      <span className="text-[8px] font-mono uppercase px-1.5 py-0.5 rounded"
                        style={{ background: `${status.color}20`, color: status.color }}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {sport && (
                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded"
                          style={{ background: `${sport.color}15`, color: sport.color }}>
                          {sport.label}
                        </span>
                      )}
                      {athlete.email && <span className="text-[10px] text-muted-foreground">{athlete.email}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-[10px] text-muted-foreground flex-shrink-0 hidden sm:flex">
                    {athlete.active_plan_name && (
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3 w-3" />
                        <span>{athlete.active_plan_name}</span>
                      </div>
                    )}
                    {athlete.last_activity_date && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        <span>{athlete.last_activity_date}</span>
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="sm"
                    className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground"
                    onClick={() => navigate('/coach/dashboard')}>
                    View
                  </Button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="rounded-lg border border-border/15 bg-card/20 p-12 text-center">
              <Users className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {athletes.length === 0 ? 'No athletes assigned yet. Athletes are added via plan instances.' : 'No athletes match your filters'}
              </p>
            </div>
          )}
        </div>
      </div>
    </CoachLayout>
  );
}
